import React from "react";
import { defineMessages, useIntl } from "react-intl";
import Case from "case";
import {
    AlertType,
    Objective,
    ParamTypes,
    Student,
} from "../../../../../../interfaces/Dashboard";
import {
    assessmentDescription,
    commonMessages,
} from "../../../../../../utils/messages";
import VisuallyHidden from "../../../../../../components/VisuallyHidden/VisuallyHidden";
import { TooltipWrapper } from "../../../../../../design-system-components/ToolTip/ToolTip";
import ProgressionBar from "../ProgressionBar/ProgressionBar";
import StudentProgressionModal from "../StudentProgressionModal/StudentProgressionModal";
import usePersistingModal from "../../../../../../hooks/ui/usePersistingModal";

import "./ObjectiveCell.scss";
import { Icon } from "@evidenceb/athena-common/design-system/Icon";
import {
    calculateAssessment,
    evaluateAlert,
    isStudentBlockedInObjective,
} from "../../../api/utils";
import classNames from "classnames";
import { useCachedStudentsAlerts } from "../../useStudentsAlerts";
import { useParams } from "react-router-dom";

interface Props {
    student: Student;
    objective: Objective;
    objectiveIndex: number;
    dataObjective: any;
    className?: string;
}

const ObjectiveCell = ({
    objective,
    student,
    objectiveIndex,
    dataObjective,
    className,
}: Props) => {
    const intl = useIntl();
    const modal = usePersistingModal(`${objective.id}/${student.id}`);
    const assessment = calculateAssessment(objective);

    const { moduleId } = useParams<ParamTypes>();
    const studentsAlerts = useCachedStudentsAlerts();
    const isBlocked = studentsAlerts?.length
        ? isStudentBlockedInObjective(
              student.id,
              objective.id,
              moduleId,
              studentsAlerts
          )
        : false;

    const tooltipContent = (
        <div className="objective-cell__tooltip__content">
            <p>{`${Case.pascal(
                intl.formatMessage(commonMessages.objectiveShort).charAt(0)
            )}${objectiveIndex + 1}. ${dataObjective.title.short}`}</p>

            {(isBlocked || assessment) && (
                <div className="objective-cell__tooltip__content__assessment">
                    {isBlocked && (
                        <p>
                            <Icon path={`alert_blocked`} />{" "}
                            {intl.formatMessage(
                                assessmentDescription[
                                    `assessment-${AlertType.BLOCKED}`
                                ]
                            )}
                        </p>
                    )}
                    {assessment && !isBlocked && (
                        <p>
                            <Icon path={`alert_${assessment}`} />
                            {intl.formatMessage(
                                assessmentDescription[
                                    `assessment-${assessment}`
                                ]
                            )}
                        </p>
                    )}
                </div>
            )}
        </div>
    );

    const handleOpenModal = () => {
        modal.setOpen(true);
    };

    return (
        <td
            className={classNames("objective-cell", className)}
            headers={"th-" + objective.id}
        >
            {modal.open && (
                <StudentProgressionModal
                    studentName={`${student.firstname} ${student.lastname}`}
                    studentId={student.id}
                    objectives={student.objectives}
                    isOpen={modal.open}
                    objective={objective}
                    onDismiss={() => modal.setOpen(false)}
                />
            )}
            <TooltipWrapper
                wrapper="div"
                tooltip={tooltipContent}
                wrapperClassname="objective-cell__tooltip__wrapper"
                showOnFocus={true}
            >
                <button
                    className="objective-cell__button"
                    onClick={handleOpenModal}
                >
                    <ProgressionBar
                        className="objective-cell__progression-bar"
                        aria-hidden={true}
                        status={objective.status}
                        alert={evaluateAlert(isBlocked, assessment)}
                    />
                    <VisuallyHidden>
                        {intl.formatMessage(
                            messages.a11ySeeObjectiveProgression,
                            { objectiveName: tooltipContent }
                        )}
                    </VisuallyHidden>
                </button>
            </TooltipWrapper>
        </td>
    );
};

export const messages = defineMessages({
    a11ySeeObjectiveProgression: {
        id: "dashboard-a11y-seeObjectiveProgression",
        defaultMessage: "Open the progression of the objective {objectiveName}",
    },
});

export default ObjectiveCell;
